import React, { useCallback, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { createFilter } from 'react-select'
import { changeDetails } from '../../../api-client/account'
import { StreetType } from '../../../api-client/enums'
import { formatPhoneNumber } from '../../../utils/format-phone-number'
import useValidation from '../../../hooks/use-validation'
import FlatLock from '../../../components/svg/flat-lock'
import { Button } from '../../../components/button'
import { FormField, Select } from '../../../components/inputs'
import {
  format,
  isValid as isDateValid,
  now,
  parse,
  parseISO,
  subYears,
  toISOString,
} from '../../../utils/date-service'
import { MINIMUM_AGE_LIMIT } from '../../../constants'
import DatePicker from '../../../components/datepicker'
import DateInput from '../../../components/date-input'

const selectStyles = {
  control: (base, state) => ({
    ...base,
    border: state.selectProps.error
      ? '0.2rem solid var(--alert-color) !important'
      : '0.2rem solid var(--secondary-input-bg) !important',
  }),
}

const maxDate = subYears(now(), MINIMUM_AGE_LIMIT)

const initialAccountDetails = {
  driverLicenseNumber: '',
  driverLicenseStateId: '',
  driverLicenseExpirationDate: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  houseNumber: '',
  unitNumber: '',
  street: '',
  streetType: '',
  city: '',
  stateId: '',
  mobileNumber: '',
}

const mapUserDetailsToState = (data) => {
  const accountDetails = {}

  Object.entries(data.accountDetails).forEach(
    ([key, value]) => (accountDetails[key] = value === null ? '' : value),
  )

  return {
    ...initialAccountDetails,
    ...accountDetails,
    driverLicenseExpirationDate: data?.accountDetails?.driverLicenseExpirationDate
      ? format(
          parseISO(data.accountDetails.driverLicenseExpirationDate, { withTime: false }),
          'MM/dd/yyyy',
        )
      : '',
    dateOfBirth: data?.accountDetails?.dateOfBirth
      ? parseISO(data?.accountDetails.dateOfBirth, { withTime: false })
      : '',
  }
}

const getStateSelectValueByStateId = (stateId, states) => {
  const state = states.find((state) => state.id === stateId)

  return state && { value: state.id, label: state.name }
}

const formatDriversLicenseExpiry = (value) => {
  if (!value) return ''

  const refinedValue = value.replace(/[^\d]/g, '')

  if (refinedValue.length < 3) return refinedValue

  if (refinedValue.length < 5) return `${refinedValue.slice(0, 2)}/${refinedValue.slice(2)}`

  return `${refinedValue.slice(0, 2)}/${refinedValue.slice(2, 4)}/${refinedValue.slice(4, 8)}`
}

const formFields = {
  DriverLicenseNumber: 'driverLicenseNumber',
  DriverLicenseStateId: 'driverLicenseStateId',
  DriverLicenseExpirationDate: 'driverLicenseExpirationDate',
  MiddleName: 'middleName',
  DateOfBirth: 'dateOfBirth',
  HouseNumber: 'houseNumber',
  UnitNumber: 'unitNumber',
  Street: 'street',
  StreetType: 'streetType',
  City: 'city',
  StateId: 'stateId',
  MobileNumber: 'mobileNumber',
}

const filterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  matchFrom: 'start',
}

const pilotPhaseStateNames = [
  'Arkansas',
  'Oklahoma',
  'Kansas',
  'Mississippi',
  'Missouri',
  'New Mexico',
  'Tennessee',
  'Texas',
]

export default function YourDetails({ prefillData, accountDetails, setAccountDetails, onNext }) {
  const { mutate, isLoading } = useMutation({
    mutationFn: changeDetails,
    onSuccess: onNext,
  })

  const [showErrors, { isFormValid, errors }] = useValidation({ accountDetails }, validate)

  useEffect(() => {
    if (prefillData?.accountDetails) {
      setAccountDetails(mapUserDetailsToState(prefillData))
    }
  }, [prefillData, setAccountDetails])

  const handleChange = (value) => (name) =>
    setAccountDetails((prev) => ({ ...prev, [name]: value }))

  const handleDateOfBirthChange = useCallback(
    (dateOfBirth) => {
      setAccountDetails((prev) => ({
        ...prev,
        dateOfBirth,
      }))
    },
    [setAccountDetails],
  )

  const handleNextClick = () => {
    if (!isFormValid) {
      showErrors()

      const timeoutRef = setTimeout(() => {
        const firstFieldWithError = document.querySelector('.error')
        if (firstFieldWithError) {
          firstFieldWithError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        clearTimeout(timeoutRef)
      }, 200)
    } else {
      mutate({
        driverLicenseNumber: accountDetails.driverLicenseNumber,
        driverLicenseStateId: accountDetails.driverLicenseStateId,
        driverLicenseExpirationDate: toISOString(
          parse(accountDetails.driverLicenseExpirationDate, 'MM/dd/yyyy', now()),
          { withTime: false },
        ),
        middleName: accountDetails.middleName,
        dateOfBirth: toISOString(accountDetails.dateOfBirth, { withTime: false }),
        houseNumber: accountDetails.houseNumber,
        unitNumber: accountDetails.unitNumber,
        street: accountDetails.street,
        streetType: accountDetails.streetType,
        city: accountDetails.city,
        stateId: accountDetails.stateId,
        mobileNumber: accountDetails.mobileNumber,
      })
    }
  }

  const generateResidenceStateOptions = () =>
    prefillData?.accountDetails?.states.reduce((acc, state) => {
      if (pilotPhaseStateNames.includes(state.name)) {
        return [...acc, { label: state.name, value: state.id }]
      } else return acc
    }, [])

  return (
    <div className='wrapper'>
      <p className='notice'>
        Please confirm your details below. We’ve pre-filled any information provided during your
        account creation. Note, you must be 18 years old to apply.
      </p>

      <div className='row flex justifyBetween'>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='licenseNumber'>Drivers License Number</label>
          {errors.driverLicenseNumber && (
            <span className='error'>{errors.driverLicenseNumber}</span>
          )}
          <FormField
            type='text'
            id='licenseNumber'
            className='formField'
            value={accountDetails.driverLicenseNumber}
            onChange={(e) => handleChange(e.target.value)(formFields.DriverLicenseNumber)}
            error={errors.driverLicenseNumber}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='issuingState'>Issuing State</label>
          {errors.driverLicenseStateId && (
            <span className='error'>{errors.driverLicenseStateId}</span>
          )}
          <Select
            styles={selectStyles}
            id='issuingState'
            className='formField'
            filterOption={createFilter(filterConfig)}
            options={prefillData?.accountDetails?.states.map((state) => ({
              label: state.name,
              value: state.id,
            }))}
            value={
              prefillData &&
              accountDetails.driverLicenseStateId &&
              getStateSelectValueByStateId(
                accountDetails.driverLicenseStateId,
                prefillData.accountDetails.states,
              )
            }
            onChange={({ value }) => handleChange(value)(formFields.DriverLicenseStateId)}
            error={errors.driverLicenseStateId}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='driversLicenseExpiry'>Expiry</label>
          {errors.driverLicenseExpirationDate && (
            <span className='error'>{errors.driverLicenseExpirationDate}</span>
          )}
          <FormField
            id='driversLicenseExpiry'
            className='formField'
            inputMode='numeric'
            placeholder='MM/DD/YYYY'
            value={formatDriversLicenseExpiry(accountDetails.driverLicenseExpirationDate)}
            onChange={(e) => handleChange(e.target.value)(formFields.DriverLicenseExpirationDate)}
            error={errors.driverLicenseExpirationDate}
          />
        </div>
      </div>

      <div className='row flex justifyBetween'>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='firstName'>First Name</label>
          <FormField
            id='firstName'
            className='formField'
            defaultValue={accountDetails.firstName}
            disabled
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='middleName'>Middle Name</label>
          <FormField
            id='middleName'
            className='formField'
            value={accountDetails.middleName}
            onChange={(e) => handleChange(e.target.value)(formFields.MiddleName)}
            error={errors.middleName}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='lastName'>Last Name</label>
          <FormField
            id='lastName'
            className='formField'
            defaultValue={accountDetails.lastName}
            disabled
          />
        </div>

        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='birthYear'>Date Of Birth</label>
          <DatePicker
            id='dateOfBirth'
            autoComplete='off'
            placeholderText='MM/DD/YYYY'
            maxDate={maxDate}
            selected={accountDetails.dateOfBirth}
            onChange={handleDateOfBirthChange}
            customInput={<DateInput inputMode='numeric' error={errors[formFields.DateOfBirth]} />}
          />
          {errors[formFields.DateOfBirth] && (
            <span className='error'>{errors[formFields.DateOfBirth]}</span>
          )}
        </div>
      </div>

      <div className='row flex justifyBetween'>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='houseNumber'>Street Number</label>
          {errors.houseNumber && <span className='error'>{errors.houseNumber}</span>}
          <FormField
            id='houseNumber'
            className='formField'
            inputMode='numeric'
            value={accountDetails.houseNumber}
            onChange={(e) => handleChange(e.target.value)(formFields.HouseNumber)}
            error={errors.houseNumber}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='unitNumber'>Unit Number</label>
          {errors.unitNumber && <span className='error'>{errors.unitNumber}</span>}
          <FormField
            id='unitNumber'
            className='formField'
            inputMode='numeric'
            value={accountDetails.unitNumber}
            onChange={(e) => handleChange(e.target.value)(formFields.UnitNumber)}
            error={errors.unitNumber}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='street'>Street Name</label>
          {errors.street && <span className='error'>{errors.street}</span>}
          <FormField
            id='street'
            className='formField'
            value={accountDetails.street}
            onChange={(e) => handleChange(e.target.value)(formFields.Street)}
            error={errors.street}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='streetType'>Street Type</label>
          {errors.streetType && <span className='error'>{errors.streetType}</span>}
          <Select
            styles={selectStyles}
            id='streetType'
            type='date'
            className='formField'
            value={
              accountDetails.streetType && {
                label: Object.keys(StreetType).find(
                  (key) => StreetType[key] === accountDetails.streetType,
                ),
                value: accountDetails.streetType,
              }
            }
            options={Object.entries(StreetType).map(([key, value]) => ({ label: key, value }))}
            onChange={({ value }) => handleChange(value)(formFields.StreetType)}
            error={errors.streetType}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='city'>City</label>
          {errors.city && <span className='error'>{errors.city}</span>}
          <FormField
            id='city'
            className='formField'
            value={accountDetails.city}
            onChange={(e) => handleChange(e.target.value)(formFields.City)}
            error={errors.city}
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='state'>State</label>
          {errors.stateId && <span className='error'>{errors.stateId}</span>}
          <Select
            styles={selectStyles}
            id='state'
            className='formField'
            filterOption={createFilter(filterConfig)}
            options={generateResidenceStateOptions()}
            error={errors.stateId}
            onChange={({ value }) => handleChange(value)(formFields.StateId)}
            value={
              prefillData &&
              getStateSelectValueByStateId(
                accountDetails.stateId,
                prefillData.accountDetails.states,
              )
            }
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='zipCode'>Postal Code</label>
          <FormField
            id='zipCode'
            className='formField'
            defaultValue={accountDetails.zipCode}
            disabled
          />
        </div>
      </div>

      <div className='row flex justifyBetween'>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='phone'>Phone</label>
          <FormField
            id='phone'
            className='formField'
            inputMode='numeric'
            defaultValue={formatPhoneNumber(accountDetails.phoneNumber)}
            disabled
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='mobile'>Mobile</label>
          <FormField
            id='mobile'
            type='tel'
            maxLength='12'
            className='formField'
            inputMode='numeric'
            value={formatPhoneNumber(accountDetails.mobileNumber)}
            error={errors.mobileNumber}
            onChange={(e) =>
              handleChange(e.target.value.replaceAll('-', ''))(formFields.MobileNumber)
            }
          />
        </div>
        <div className='fieldWrapper flex flexDirectionColumn'>
          <label htmlFor='email'>Email Address</label>
          <FormField
            id='email'
            className='formField'
            defaultValue={accountDetails.email}
            disabled
          />
        </div>
      </div>

      <span className='securityNotice flex alignCenter'>
        <FlatLock />
        <p>Your information isn’t shared with other people who use privauto.</p>
      </span>

      <div className='nextBtnContainer flex justifyEnd'>
        <Button disabled={isLoading} onClick={handleNextClick}>
          Next
        </Button>
      </div>

      <style jsx>{`
        .wrapper {
          .row {
            position: relative;
            flex-wrap: wrap;
            margin: 2.1rem 0 0;
            padding: 0 0 3.1rem;
            border-bottom: 1px solid var(--separator-line-color);

            :last-of-type {
              border: none;
            }
          }

          .fieldWrapper {
            max-width: 40rem;
            width: 100%;
            margin: 1.9rem 0 0;

            label {
              color: var(--secondary-text-color);
              margin: 0 0 1.2rem;
              line-height: 2.1rem;
              font-size: 1.6rem;
              font-weight: bold;
            }

            .formField {
              &.datePicker {
                max-width: 40rem;
                width: 100%;
              }
            }
          }
        }

        .notice {
          line-height: 2.1rem;
          font-size: 1.6rem;
        }

        .securityNotice {
          margin: 3.3rem auto 0;
          width: 100%;

          :global(svg) {
            margin: 0 1.16rem 0 0;
          }

          p {
            margin: 0;
            font-size: 1.4rem;
            font-style: italic;
          }
        }

        .error {
          display: block;
          margin: 0 0 1rem;
          color: var(--alert-color);
          font-size: 1.4rem;
        }

        .nextBtnContainer {
          margin: 1.3rem 0 0;
        }

        @media (max-width: 1024px) {
          .wrapper .row {
            flex-direction: column;
            align-items: center;
          }

          .nextBtnContainer {
            margin: 3rem 0 0;

            :global(button) {
              width: 100%;
            }
          }
        }
      `}</style>
    </div>
  )
}

function validate({ accountDetails: state }) {
  const errors = {}
  let isValid = true

  if (state.driverLicenseNumber?.length < 1) {
    errors[formFields.DriverLicenseNumber] = 'required'
  }

  if (state.driverLicenseStateId?.length < 1) {
    errors[formFields.DriverLicenseStateId] = 'required'
  }

  if (state.driverLicenseExpirationDate?.length < 1) {
    errors[formFields.DriverLicenseExpirationDate] = 'required'
  }

  if (
    state.driverLicenseExpirationDate?.length > 1 &&
    !isDateValid(parse(state.driverLicenseExpirationDate, 'MM/dd/yyyy', now()))
  ) {
    errors[formFields.DriverLicenseExpirationDate] = 'Date is not valid'
  }

  if (!state.dateOfBirth) {
    errors[formFields.DateOfBirth] = 'required'
  }

  if (state.houseNumber?.length < 1) {
    errors[formFields.HouseNumber] = 'required'
  }

  if (!state.streetType) {
    errors[formFields.StreetType] = 'required'
  }

  if (state.street?.length < 1) {
    errors[formFields.Street] = 'required'
  }

  if (state.city?.length < 1) {
    errors[formFields.City] = 'required'
  }

  if (state.stateId?.length < 1) {
    errors[formFields.StateId] = 'required'
  }

  if (state.mobileNumber.length > 0 && state.mobileNumber.length !== 10) {
    errors[formFields.MobileNumber] = true
  }

  if (Object.entries(errors).length > 0) isValid = false

  return { isValid, errors }
}
